
import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { supabase } from "@/integrations/supabase/client";

// Define the type for the auth context
type AuthContextType = {
  user: any | null;
  userProfile: any | null;
  loading: boolean;
  initialized: boolean; // Add initialized flag
};

// Create a context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  loading: true,
  initialized: false, // Initialize as false
});

// Create a provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false); // Add initialized state

  useEffect(() => {
    // Function to fetch user profile
    const fetchUserProfile = async (userId: string) => {
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
        
      setUserProfile(data);
    };

    // Set up the auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user || null);
      
      if (session?.user?.id) {
        fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
      setInitialized(true); // Mark as initialized
    });
    
    // THEN get the initial session
    const getSession = async () => {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
      
      if (session?.user?.id) {
        await fetchUserProfile(session.user.id);
      }
      setLoading(false);
      setInitialized(true); // Mark as initialized
    };
    
    getSession();
    
    // Clean up subscription on unmount
    return () => subscription.unsubscribe();
  }, []);

  // Provide the auth context to children
  return (
    <AuthContext.Provider value={{ user, userProfile, loading, initialized }}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use the auth context
export const useAuth = () => useContext(AuthContext);
